// Топбар (SPEC розд. 11): бургер (мобільні), пошук Ctrl+K, вибір навчального року/семестру, тема, дзвіночок, профіль.
import { ROLE_LABELS } from '@unisched/shared';
import { Bell, LogOut, Menu, Monitor, Moon, Sun, UserRound } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/cn';
import { nextTheme, THEME_LABELS, useTheme } from '@/lib/theme';
import { SemesterPicker } from '@/features/contingent/SemesterPicker';
import { NotificationsBell as NotificationsCenter } from '@/features/admin/NotificationsBell';
import { Dropdown, EmptyState, IconButton, MenuItem, MenuLabel, MenuSeparator, Select } from '@/ui';
import { SearchPalette } from '@/features/analytics';
import { academicYearCodeOf, semesterLabel, useSemester, type SemesterItem } from './semester';

/** Підпис семестру у виборі: назва з довідника; рік додається, лише якщо його немає в назві. */
function optionLabel(s: SemesterItem, year: string): string {
  const label = semesterLabel(s);
  const y = year.replace('-', '/');
  return label.includes(y) || label.includes(year) ? label : `${y} · ${label}`;
}

function SemesterSelect() {
  const { semesters, semester, setSemester, isLoading, error } = useSemester();
  if (error || (!isLoading && !semesters.length)) {
    return (
      <span className="hidden truncate rounded-field border border-dashed border-border px-3 py-1.5 text-[13px] text-muted sm:inline" title="Додайте семестри в довіднику «Семестри»">
        Семестр не задано
      </span>
    );
  }
  // Групи за навчальним роком, у межах року — за номером семестру.
  const years = new Map<string, typeof semesters>();
  for (const s of semesters) {
    const y = academicYearCodeOf(s) ?? '';
    years.set(y, [...(years.get(y) ?? []), s]);
  }
  const groups = [...years.entries()].map(([year, list]) => ({
    label: year ? `${year.replace('-', '/')} навчальний рік` : 'Навчальний рік',
    options: [...list].sort((a, b) => a.number - b.number).map((s) => ({ value: s.id, label: optionLabel(s, year) })),
  }));
  return (
    <Select
      size="sm"
      aria-label="Навчальний рік і семестр"
      className="num w-[8.5rem] truncate sm:w-60"
      value={semester?.id ?? ''}
      disabled={isLoading}
      onChange={(e) => setSemester(e.target.value)}
      groups={groups}
      placeholder={isLoading ? 'Завантаження…' : undefined}
    />
  );
}

function ThemeToggle() {
  const [theme, setTheme] = useTheme();
  const Icon = theme === 'light' ? Sun : theme === 'dark' ? Moon : Monitor;
  return <IconButton icon={Icon} className="hidden sm:inline-flex" label={`${THEME_LABELS[theme]} (змінити)`} onClick={() => setTheme(nextTheme(theme))} />;
}

function NotificationsBell() {
  // Заглушка: сповіщення (D26) підключить відповідний модуль (GET /api/notifications).
  return (
    <Dropdown aria-label="Сповіщення" trigger={(p) => <IconButton {...p} icon={Bell} label="Сповіщення" />}>
      {() => (
        <div className="w-72">
          <MenuLabel>Сповіщення</MenuLabel>
          <EmptyState compact icon={Bell} title="Сповіщень немає" description="Тут з'являться повідомлення про публікацію розкладу, зміни й погодження." />
        </div>
      )}
    </Dropdown>
  );
}

function ProfileMenu() {
  const { user, logout } = useAuth();
  const [theme, setTheme] = useTheme();
  const ThemeIcon = theme === 'light' ? Sun : theme === 'dark' ? Moon : Monitor;
  if (!user) return null;
  const initials =
    user.fullName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase())
      .join('') || '?';
  const roles = [...new Set(user.roles.map((r) => r.role))];
  return (
    <Dropdown
      aria-label="Профіль"
      trigger={(p) => (
        <button
          {...p}
          type="button"
          className="flex h-9 items-center gap-2 rounded-field pr-2 pl-1 hover:bg-surface-2"
          aria-label={`Профіль: ${user.fullName}`}
        >
          <span className="flex size-7 items-center justify-center rounded-full bg-primary-soft text-[12px] font-semibold text-primary-ink">{initials}</span>
          <span className="hidden max-w-40 truncate text-[13px] font-semibold md:inline">{user.fullName}</span>
        </button>
      )}
    >
      {(close) => (
        <div className="w-64">
          <div className="flex items-start gap-2.5 px-2.5 pt-2 pb-2.5">
            <UserRound className="mt-0.5 size-4 shrink-0 text-muted" aria-hidden />
            <div className="min-w-0">
              <p className="truncate text-[14px] font-semibold">{user.fullName}</p>
              <p className="truncate text-[12px] text-muted">{user.email}</p>
              {roles.length ? <p className="mt-1 text-[12px] text-fg-2">{roles.map((r) => ROLE_LABELS[r] ?? r).join(', ')}</p> : null}
            </div>
          </div>
          <MenuSeparator />
          <MenuItem icon={ThemeIcon} onSelect={() => setTheme(nextTheme(theme))}>
            {THEME_LABELS[theme]}
          </MenuItem>
          <MenuItem
            icon={LogOut}
            danger
            onSelect={() => {
              close();
              void logout();
            }}
          >
            Вийти
          </MenuItem>
        </div>
      )}
    </Dropdown>
  );
}

// Маркери модулів (BUILD §5): вибір семестру — U04, пошук Ctrl+K — U15, дзвіночок — U16; модуль замінює елемент
// між своїми маркерами (один імпорт + один елемент).
export function Topbar({ onOpenMenu }: { onOpenMenu: () => void }) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return (
    <header
      className={cn(
        'sticky top-0 z-30 flex h-14 items-center gap-2 border-b bg-surface px-3 sm:px-4',
        scrolled ? 'border-border' : 'border-divider',
      )}
    >
      <IconButton icon={Menu} label="Відкрити меню" className="lg:hidden" onClick={onOpenMenu} />
      {/* <U15> */}
      <SearchPalette />
      {/* </U15> */}
      <div className="ml-auto flex min-w-0 items-center gap-1 sm:gap-1.5">
        {/* <U04> */}
        <SemesterPicker fallback={<SemesterSelect />} />
        {/* </U04> */}
        <ThemeToggle />
        {/* <U16> */}
        <NotificationsCenter fallback={<NotificationsBell />} />
        {/* </U16> */}
        <ProfileMenu />
      </div>
    </header>
  );
}
